import { DashboardView } from "./dashboard/DashboardView";
import { useEffect, useState } from "react";
import {
  api,
  clearOwnerBrowserAuth,
  hasOwnerBrowserAuth,
  markOwnerSessionActive,
  OWNER_SESSION_INVALIDATED_EVENT,
  type OwnerSessionInvalidatedDetail,
} from "./api";
import { isStandaloneSimoTMode } from "./appMode";
import { LoginPage } from "./LoginPage";
import { SimoTDesktopApp } from "./SimoTDesktopApp";

function replaceWithLogin(reason?: OwnerSessionInvalidatedDetail["reason"]): void {
  if (typeof window === "undefined" || window.location.pathname === "/login") return;
  const next = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams();
  if (next !== "/") params.set("next", next);
  if (reason) params.set("reason", reason);
  const query = params.toString();
  window.history.replaceState({}, "", `/login${query ? `?${query}` : ""}`);
}

// SIMO oeffnet direkt als Voice-first Agent-OS-Shell: Mission Workspace als
// Primaerflaeche, Live Ops/Evidence/Code Workspace als Werkzeuge dahinter,
// Command unten und Einstellungen als Drawer. Die klassische ChatView bleibt
// als Hook-Consumer erhalten, ist aber nicht mehr die Primaeroberflaeche.
export function App(): JSX.Element {
  const envMode = import.meta.env.VITE_SIMO_APP_MODE;
  const search = typeof window === "undefined" ? "" : window.location.search;
  const path = typeof window === "undefined" ? "/" : window.location.pathname;
  const [ownerSessionState, setOwnerSessionState] = useState<"checking" | "authenticated" | "missing">(() =>
    path === "/login" ? "missing" : hasOwnerBrowserAuth() ? "checking" : "checking",
  );
  useEffect(() => {
    const onInvalidated = (event: Event) => {
      const detail = (event as CustomEvent<OwnerSessionInvalidatedDetail>).detail;
      setOwnerSessionState("missing");
      replaceWithLogin(detail?.reason ?? "invalid_session");
    };
    window.addEventListener(OWNER_SESSION_INVALIDATED_EVENT, onInvalidated);
    const onStorage = () => setOwnerSessionState(hasOwnerBrowserAuth() ? "checking" : "missing");
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(OWNER_SESSION_INVALIDATED_EVENT, onInvalidated);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  useEffect(() => {
    if (isStandaloneSimoTMode(search, envMode) || path === "/login" || typeof window === "undefined") return;
    let cancelled = false;
    setOwnerSessionState("checking");
    void api.session()
      .then(() => {
        if (cancelled) return;
        markOwnerSessionActive();
        setOwnerSessionState("authenticated");
      })
      .catch(() => {
        if (cancelled) return;
        clearOwnerBrowserAuth();
        setOwnerSessionState("missing");
        replaceWithLogin("missing_session");
      });
    return () => {
      cancelled = true;
    };
  }, [envMode, path, search]);
  if (isStandaloneSimoTMode(search, envMode)) {
    return <SimoTDesktopApp />;
  }
  if (path === "/login" || ownerSessionState !== "authenticated") {
    return <LoginPage />;
  }
  return <DashboardView />;
}
